import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

const getText = (value) => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(getText).join("");
  }

  if (value?.props?.children) {
    return getText(value.props.children);
  }

  return "";
};

const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[\s]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/--+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeImageSrc = (src) => {
  if (typeof src !== "string") {
    return "";
  }

  const trimmed = src.trim();

  if (!trimmed || /^(data:|blob:|javascript:)/i.test(trimmed)) {
    return "";
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  try {
    if (typeof window !== "undefined") {
      return new URL(trimmed, window.location.origin).toString();
    }
  } catch {
    return "";
  }

  return trimmed;
};

const buildImagePreviewUrl = (src, alt) => {
  if (typeof window === "undefined" || !src) {
    return "";
  }

  const params = new URLSearchParams({
    src,
    alt: alt || "",
  });

  return `${window.location.origin}/help-image-preview.html?${params.toString()}`;
};

const ArticleContent = ({ content }) => {
  return (
    <div className="help-article-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          h2: ({ children, ...props }) => {
            const text = getText(children);
            const id = slugify(text);
            return (
              <h2 id={id} {...props}>
                {children}
              </h2>
            );
          },
          h3: ({ children, ...props }) => {
            const text = getText(children);
            const id = slugify(text);
            return (
              <h3 id={id} {...props}>
                {children}
              </h3>
            );
          },
          h4: ({ children, ...props }) => {
            const text = getText(children);
            const id = slugify(text);
            return (
              <h4 id={id} {...props}>
                {children}
              </h4>
            );
          },
          a: ({ children, href, ...props }) => {
            const isExternal = href?.startsWith("http");
            return (
              <a
                href={href}
                className="help-inline-link"
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noreferrer" : undefined}
                {...props}
              >
                {children}
              </a>
            );
          },
          ul: ({ children, ...props }) => (
            <ul className="help-markdown-list" {...props}>
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol className="help-markdown-list" {...props}>
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            <li className="help-markdown-list-item" {...props}>
              {children}
            </li>
          ),
          p: ({ children, ...props }) => (
            <p className="help-markdown-paragraph" {...props}>
              {children}
            </p>
          ),
          img: ({ src, alt, ...props }) => (
            (() => {
              const resolvedSrc = normalizeImageSrc(src);

              if (!resolvedSrc) {
                return null;
              }

              return (
                <a
                  href={resolvedSrc}
                  className="help-article-image-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open image in new tab${alt ? `: ${alt}` : ""}`}
                  title="Open image in new tab"
                  onClick={(event) => {
                    event.preventDefault();
                    const previewUrl = buildImagePreviewUrl(resolvedSrc, alt);
                    if (!previewUrl) {
                      return;
                    }

                    window.open(previewUrl, "_blank", "noopener,noreferrer");
                  }}
                >
                  <img
                    src={resolvedSrc}
                    alt={alt || ""}
                    className="help-article-image"
                    loading="lazy"
                    {...props}
                  />
                </a>
              );
            })()
          ),
          strong: ({ children, ...props }) => (
            <strong className="help-markdown-strong" {...props}>
              {children}
            </strong>
          ),
          code: ({ children, ...props }) => (
            <code className="help-markdown-inline-code" {...props}>
              {children}
            </code>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default ArticleContent;
